import React from 'react';
import {
    Caption,
    Flex,
    Grid,
    Image,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import type { CartItem as CartItemType } from '../../type/cart-item';
import * as S from './CartItem.style';
import { getVariantDescription } from '../../util/get-variant-description';

interface Props {
    data: CartItemType;
}

const CartItem = ({ data }: Props) => {
    const imageSrc = data.Variant.Thumbnail && data.Variant.Thumbnail?.Desktop;
    const optionsLabel = getVariantDescription(data.Variant);
    const theme = useTheme();
    const { mobile } = useAppDisplayModeContext();
    const { css } = useStyles({ isSample: data.Sample });

    return (
        <Grid
            as="li"
            className={css(S.CartItem)}
            columns={`${pxToRem(64)} 1fr ${pxToRem(mobile ? 38 : 32)}`}
        >
            <Image
                className={css(S.Image)}
                src={imageSrc || ''}
                width={'100%'}
                height={'100%'}
                alt={optionsLabel || ''}
            />
            <div>
                <div className={css(S.ProductTitleContainer)}>
                    <Caption
                        className={css(S.ProductTitle)}
                        weight="medium"
                        variant="03"
                        maximumLines={2}
                    >
                        {data.Variant.Product.Title}
                    </Caption>
                    {optionsLabel && (
                        <Caption
                            variant="04"
                            weight="regular"
                            variables={{
                                captionColor:
                                    theme.siteVariables.colors.grey[300],
                            }}
                            className={css(S.OptionLabel)}
                        >
                            {optionsLabel}
                        </Caption>
                    )}
                </div>
                <Flex className={css(S.Pricing)}>
                    <div className={css(S.Price)}>
                        {data.Price.ShortFormat ===
                        data.RetailPrice.ShortFormat ? (
                            <Caption
                                variant="04"
                                weight="medium"
                                className={css(S.SalePrice)}
                            >
                                {data.Price.ShortFormat}
                            </Caption>
                        ) : (
                            <>
                                <Caption
                                    variant="04"
                                    weight="medium"
                                    className={css(S.RetailPrice)}
                                    as="span"
                                >
                                    {data.RetailPrice.ShortFormat}
                                </Caption>
                                <Caption
                                    variant="04"
                                    weight="medium"
                                    className={css(S.SalePrice)}
                                    as="span"
                                >
                                    {data.Price.ShortFormat}
                                </Caption>
                            </>
                        )}
                    </div>
                    {data.Sample ? (
                        <Caption
                            className={css(S.SampleTag)}
                            variant="05"
                            weight="medium"
                        >
                            SAMPLE
                        </Caption>
                    ) : (
                        <span></span>
                    )}
                </Flex>
            </div>
            <Flex vAlign="center" className={css(S.Quantity)}>
                <Caption weight="medium" variant="04">
                    {'x' + data.Quantity}
                </Caption>
            </Flex>
        </Grid>
    );
};

export default CartItem;
